















































































































































































































import Vue, { PropType } from "vue";
import VaCard from "@/components/molecules/VaCard.vue";
import {
  ExternalProvider,
  ExchangeConfigurationForm,
  createExchangeConfigurationForm,
  CalDavConfigurationForm,
  createCalDavConfigurationForm,
  ProviderAccount,
  GoogleCalendarItem,
  FeatureType,
  ProviderAccountItem,
  FeaturePayload,
} from "@/models/providerAccount";
import VaButton from "@/components/atoms/VaButton.vue";
import RoomService from "@/services/roomService";
import {
  supportedAccounts,
  fetchGoogleCalendars,
  findAccountByProvider,
  getProviderActivationFeature,
  toggleFeature,
  openLoginPopup,
  openZoomLoginWithLogout,
  isSafari,
  translateOr,
  liveCollaborationAccountConfig,
  liveCollaborationAccountName,
} from "@/services/externalProvidersService";
import { RoomCommandTypes, RoomDetail } from "@/models/room";
import VaNotification from "@/components/molecules/VaNotification.vue";
import VaSwitch from "@/components/atoms/VaSwitch.vue";
import SafariLoginDialog from "@/components/organisms/safariLoginDialog.vue";
import ExchangeConfigurationDialog from "@/components/organisms/rooms/ExchangeConfigurationDialog.vue";
import NotificationService from "@/services/notificationService";
import LiveCollaborationConfigurationDialog from "@/components/organisms/rooms/LiveCollaborationConfigurationDialog.vue";

// import VaAvatar from "@/components/atoms/VaAvatar.vue";

export default Vue.extend({
  name: "RemoteProviders",
  components: {
    VaCard,
    VaButton,
    VaNotification,
    VaSwitch,
    SafariLoginDialog,
    ExchangeConfigurationDialog,
    LiveCollaborationConfigurationDialog,
    // VaAvatar,
  },
  props: {
    room: {
      type: Object as PropType<RoomDetail>,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      selectedCalendar: undefined as ExternalProvider | undefined,
      selectedGoogleCalendarId: null as string | null,
      googleCalendars: [] as GoogleCalendarItem[],
      googleCalendarsLoading: false,
      selectedInstantMeetings: [] as ExternalProvider[],
      // Login dialog state for Safari iOS popup blocking workaround
      pendingLoginUrl: null as string | null,
      pendingProvider: null as ExternalProvider | null,
      exchangeDialogOpen: false,
      exchangeConfig: createExchangeConfigurationForm(),
      liveCollaborationDialogOpen: false,
      // Key to force switch re-render after OAuth error
      switchResetKey: 0,
    };
  },
  methods: {
    /** One Live Collaboration feature change; the room is refreshed with the backend's answer. */
    async toggleLiveCollaborationFeature(
      feature: FeatureType,
      enabled: boolean,
      config?: Record<string, string>
    ) {
      const payload = {
        provider: "LiveCollaboration",
        feature,
        enabled,
        config,
      } as unknown as FeaturePayload;
      const room = await toggleFeature(payload, this.room.id);
      this.$store.commit("room/updateRoom", room);
    },
    /**
     * Live Collaboration has no OAuth: "Connect" asks for the account in a dialog and sends it
     * with the provider's Default feature. The backend has the room check it first, then keeps
     * it encrypted on the external account. A refusal is shown and rethrown, so the dialog stays
     * open with what was typed.
     */
    async onLiveCollaborationDialogSubmit(config: CalDavConfigurationForm) {
      try {
        await this.toggleLiveCollaborationFeature(
          "Default",
          true,
          liveCollaborationAccountConfig(config)
        );
      } catch (e) {
        this.notifyLiveCollaborationError(
          e,
          "providers.liveCollaboration.connectFailed",
          "The Live Collaboration account could not be connected. Try again."
        );
        throw e;
      }
    },
    /**
     * A Live Collaboration refusal in words. EA402 is the room's own check failing: wrong
     * account, room offline, or a network the calendar server does not accept. A code without
     * a translation gets the given fallback instead of the raw "errorCode.X" key.
     */
    notifyLiveCollaborationError(
      e: unknown,
      fallbackKey: string,
      fallback: string
    ) {
      const code = (e as { response?: { data?: { errorCode?: string } } })
        ?.response?.data?.errorCode;
      if (code === "EA402") {
        NotificationService.error(
          translateOr(
            "providers.liveCollaboration.connectRefused",
            "The room could not sign in to the Live Collaboration calendar with this username and password. Check them, and make sure the room is online."
          )
        );
      } else if (code && this.$te(`errorCode.${code}`)) {
        NotificationService.error(e as never);
      } else {
        NotificationService.error(translateOr(fallbackKey, fallback));
      }
    },
    /**
     * Disconnecting turns the calendar off first (it needs the account), then logs the external
     * account out like the other providers — stopping at the first refusal, so the calendar is
     * never left on without an account. Joining by ID and PIN needs no account and stays.
     */
    async disconnectLiveCollaboration() {
      const account = this.room.externalAccounts?.accounts?.find(
        (acc: ProviderAccount) => acc.provider === "LiveCollaboration"
      );
      const hasFeature = (feature: FeatureType) =>
        account?.features.some((f) => f.featureType === feature) ?? false;
      try {
        if (hasFeature("Calendar")) {
          await this.toggleLiveCollaborationFeature("Calendar", false);
        }
        if (account?.podExternalAccountId) {
          const room = await RoomService.logoutToProvider(
            account.podExternalAccountId,
            this.room.id
          );
          this.$store.commit("room/updateRoom", room);
        }
      } catch (e) {
        this.notifyLiveCollaborationError(
          e,
          "providers.liveCollaboration.disconnectFailed",
          "The Live Collaboration account could not be disconnected. Try again."
        );
        await this.$store.dispatch("room/fetchRoom", this.room.id);
      }
    },
    getVisibleFeatures(provider: ProviderAccountItem): FeatureType[] {
      const capabilities =
        this.room.supportedCapabilities?.externalAccount ?? null;
      if (!capabilities) {
        return this.room.deviceType === "iOS" ? [] : provider.features;
      }
      const providerCaps = capabilities[provider.slug];
      if (!providerCaps) return provider.features;
      const calendarFeatures: FeatureType[] = ["Calendar"];
      const meetingFeatures: FeatureType[] = [
        "JoinMeeting",
        "HostMeeting",
        "AuthCall",
        "Chat",
        "Default",
      ];
      const visible: FeatureType[] = [];
      if (providerCaps.calendar) {
        visible.push(
          ...provider.features.filter((f) => calendarFeatures.includes(f))
        );
      }
      if (providerCaps.meeting) {
        visible.push(
          ...provider.features.filter((f) => meetingFeatures.includes(f))
        );
      }
      return visible;
    },
    getFeatureText(provider: ProviderAccountItem, feature: FeatureType) {
      if (provider.provider === "Google" && feature === "JoinMeeting") {
        return this.$t("providers.features.JoinMeetingCode");
      }
      if (provider.provider === "OpenVisio" && feature === "JoinMeeting") {
        // OpenVisio joins by meeting ID only — no passcode. The runtime
        // catalogue is replaced by the published Tolgee export, so fall back
        // to the generic label while the key is missing there ($t returns
        // the key itself when unresolved).
        const label = this.$t("providers.features.JoinMeetingId") as string;
        return label === "providers.features.JoinMeetingId"
          ? this.$t("providers.features.JoinMeeting")
          : label;
      }
      if (
        provider.provider === "LiveCollaboration" &&
        feature === "JoinMeeting"
      ) {
        return translateOr(
          "providers.liveCollaboration.joinMeeting",
          "Join meetings via ID and PIN"
        );
      }
      return this.$t(`providers.features.${feature}`);
    },
    connectedAs(provider: ProviderAccountItem): string {
      const account = this.getAccount(provider);
      if (provider.provider === "LiveCollaboration") {
        return liveCollaborationAccountName(account) ?? "";
      }
      return account?.email ?? "";
    },
    isEnabled(provider: ProviderAccountItem) {
      const accounts = this.room.externalAccounts?.accounts;
      if (!accounts) return false;
      if (provider.provider === "LiveCollaboration") {
        return !!liveCollaborationAccountName(this.getAccount(provider));
      }
      return (
        this.dontNeedConnection(provider) ||
        accounts.some(
          (account: ProviderAccount) =>
            account.provider === provider.provider && account.email != null
        )
      );
    },
    dontNeedConnection(provider: ProviderAccountItem): boolean {
      return provider.provider == "Mago" || provider.provider == "OpenVisio";
    },
    dependsOnAuthcall(
      feature: FeatureType,
      provider: ProviderAccountItem
    ): boolean {
      return (
        (provider.provider == "Microsoft" || provider.provider == "Google") &&
        (feature === "HostMeeting" || feature === "Chat")
      );
    },
    hasDependencyMeeted(feature: FeatureType, provider: ProviderAccountItem) {
      if (feature == "AuthCall" && provider.provider == "Google") {
        return false;
      }

      const account = this.room.externalAccounts?.accounts?.find(
        (acc: ProviderAccount) => acc.provider === provider.provider
      );
      // const hasHostMeetingEnabled =
      //   account?.features.some((f) => f.featureType === "HostMeeting") ?? false;
      // if (hasHostMeetingEnabled) {
      //   if (feature === "JoinMeeting" && hasHostMeetingEnabled) {
      //     return false;
      //   }
      // }

      const hasAuthCallEnabled =
        account?.features.some((f) => f.featureType === "AuthCall") ?? false;
      if (provider.features.includes("AuthCall") && hasAuthCallEnabled) {
        return true;
      }

      return !this.dependsOnAuthcall(feature, provider);
    },
    isFeatureEnabled(feature: FeatureType, provider: ProviderAccountItem) {
      const account = this.getAccount(provider);
      if (!account) return false;
      return account.features.some((f) => f.featureType === feature);
    },
    getAccount(provider: ProviderAccountItem): ProviderAccount | undefined {
      return this.room.externalAccounts?.accounts.find(
        (account: ProviderAccount) => account.provider === provider.provider
      );
    },
    onExchangeActionClick() {
      this.exchangeDialogOpen = true;
    },
    async onExchangeDialogSubmit(config: ExchangeConfigurationForm) {
      this.exchangeConfig = {
        ...config,
      };
      await this.onFeatureToggle(
        "Calendar",
        "MicrosoftExchange",
        true,
        this.exchangeConfig
      );
    },
    async onToggleActivation(provider: ExternalProvider) {
      if (provider === "LiveCollaboration") {
        if (this.loading) return;
        if (this.isLiveCollaborationConnected) {
          this.loading = true;
          await this.disconnectLiveCollaboration();
          this.loading = false;
        } else if (!this.room.online) {
          // The room itself checks the account before it is saved: offline, it always fails.
          NotificationService.warning(
            translateOr(
              "providers.liveCollaboration.roomOffline",
              "The room must be online to connect a Live Collaboration account: it checks the account before it is saved."
            )
          );
        } else {
          this.liveCollaborationDialogOpen = true;
        }
        return;
      }
      this.loading = true;
      const accoutProvider = findAccountByProvider(provider, this.room);
      if (accoutProvider && accoutProvider.podExternalAccountId) {
        const room = await RoomService.logoutToProvider(
          accoutProvider.podExternalAccountId,
          this.room.id
        );
        this.$store.commit("room/updateRoom", room);
      } else {
        this.onFeatureToggle(
          getProviderActivationFeature(provider),
          provider,
          true
        );
      }
      this.loading = false;
    },
    async onSendCommand() {
      if (!this.podConfig || !this.room.online) return;
      this.loading = true;
      const payload = this.buildProvidersConfigPayload();
      await RoomService.sendCommand(
        RoomCommandTypes.SetOauthConfiguration,
        payload
      );
      this.loading = false;
    },
    buildProvidersConfigPayload() {
      if (!this.podConfig) return this.podConfig;
      return {
        ...this.podConfig,
        accounts: this.podConfig.accounts.map((account) => {
          const configuration = (account as any).configuration;
          const hasCalendarFeature = account.features.some(
            (feature) => feature.featureType === "Calendar"
          );
          if (!configuration?.calendar || !hasCalendarFeature) return account;
          const features = account.features.map((feature) => {
            if (feature.featureType === "Calendar") {
              return {
                ...feature,
                configuration: {
                  calendarId: configuration.calendar.calendarId,
                },
              };
            }
            return feature;
          });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { configuration: _omit, ...rest } = account as any;
          return { ...rest, features };
        }),
      };
    },
    async onFeatureToggle(
      feature: FeatureType,
      provider: ExternalProvider,
      enabled: boolean,
      config?: unknown
    ) {
      try {
        const featurePayload = {
          provider: provider,
          feature,
          enabled,
          config,
        } as FeaturePayload;

        if (feature === "Calendar") {
          if (enabled) {
            this.selectedCalendar = provider;
            if (provider == "Google" && !config) {
              featurePayload.config = null;
              this.loadGoogleCalendars();
            }
          } else {
            if (this.selectedCalendar === provider) {
              this.selectedCalendar = "None";
            }
          }
        }
        const room = await toggleFeature(featurePayload, this.room.id);
        this.$store.commit("room/updateRoom", room);
      } catch (e) {
        // Force switch to reset to original value by changing the key
        this.switchResetKey++;
        const errorCode = (e as any)?.response?.data?.errorCode;
        if (errorCode === "EA401") {
          const loginUrl = (e as any)?.response?.data?.errorData?.loginUrl;
          this.handleOAuthLogin(provider, loginUrl);
        }
      }
    },
    handleOAuthLogin(provider: ExternalProvider, loginUrl: string) {
      if (isSafari()) {
        // Safari blocks popups after await, show dialog for user to click
        this.pendingLoginUrl = loginUrl;
        this.pendingProvider = provider;
      } else {
        if (provider === "Zoom") {
          openZoomLoginWithLogout(loginUrl, () => this.onPopupFeatureClosed());
        } else {
          openLoginPopup(loginUrl, () => this.onPopupFeatureClosed());
        }
      }
    },
    async onPopupFeatureClosed() {
      await this.$store.dispatch("room/fetchRoom", this.room.id);
      this.pendingLoginUrl = null;
      this.pendingProvider = null;
    },
    async onSelectGoogleCalendar(val: string) {
      return this.onFeatureToggle("Calendar", "Google", true, {
        // calendar: { calendarId: val },
        calendarId: val,
      });
    },
    async loadGoogleCalendars() {
      try {
        this.googleCalendarsLoading = true;
        let cal;
        [this.googleCalendars, cal] = await fetchGoogleCalendars(this.room);
        if (!this.selectedGoogleCalendarId) {
          this.selectedGoogleCalendarId = cal;
        }
      } finally {
        this.googleCalendarsLoading = false;
      }
    },
  },
  computed: {
    isLiveCollaborationConnected(): boolean {
      return !!liveCollaborationAccountName(
        this.room.externalAccounts?.accounts?.find(
          (acc: ProviderAccount) => acc.provider === "LiveCollaboration"
        )
      );
    },
    /** The connected account, to pre-fill the dialog: the user name only, never the password. */
    liveCollaborationStoredConfig(): CalDavConfigurationForm {
      const account = this.room.externalAccounts?.accounts?.find(
        (acc: ProviderAccount) => acc.provider === "LiveCollaboration"
      );
      return {
        ...createCalDavConfigurationForm(),
        username: liveCollaborationAccountName(account) ?? "",
      };
    },
    providers() {
      const capabilities =
        this.room.supportedCapabilities?.externalAccount ?? null;
      const base = supportedAccounts.filter(
        (account) => account.provider !== "MicrosoftExchange"
      );
      if (!capabilities) {
        // Legacy fallback (device never declared capabilities): frozen to the historical
        // provider set — capability-gated providers would silently not work on old builds.
        return this.room.deviceType === "iOS"
          ? []
          : base.filter((account) => !account.requiresCapability);
      }
      return base.filter((account) => account.slug in capabilities);
    },
    exchange() {
      const capabilities =
        this.room.supportedCapabilities?.externalAccount ?? null;
      const exchangeAccount = supportedAccounts.find(
        (account) => account.provider === "MicrosoftExchange"
      );
      if (!exchangeAccount) return null;
      // Legacy: hide exchange when no capabilities
      if (!capabilities) return null;
      // New: show exchange only if its slug is in capabilities
      return exchangeAccount.slug in capabilities ? exchangeAccount : null;
    },
    podConfig() {
      return this.room.externalAccounts;
    },
    microsoftHostMeetingNeedsAuthCall(): boolean {
      const microsoftAccount = this.room.externalAccounts?.accounts?.find(
        (account: ProviderAccount) => account.provider === "Microsoft"
      );
      if (!microsoftAccount) return false;
      const hasHostMeeting = microsoftAccount.features.some(
        (feature) => feature.featureType === "HostMeeting"
      );
      const hasAuthCall = microsoftAccount.features.some(
        (feature) => feature.featureType === "AuthCall"
      );
      return hasHostMeeting && !hasAuthCall;
    },
    isZoomConnected(): boolean {
      const zoomProvider = this.providers.find(
        (p: ProviderAccountItem) => p.provider === "Zoom"
      );
      return zoomProvider ? this.isEnabled(zoomProvider) : false;
    },
  },
  watch: {
    podConfig: {
      immediate: true,
      async handler() {
        const accounts = this.room.externalAccounts?.accounts ?? [];
        const enabledCalendarAccount = accounts.find(
          (account) =>
            account.provider !== "LiveCollaboration" &&
            account.features.some(
              (feature) => feature.featureType === "Calendar"
            )
        );
        const nextValue =
          enabledCalendarAccount?.configuration?.calendar?.calendarId;
        if (nextValue && this.selectedGoogleCalendarId !== nextValue) {
          this.selectedGoogleCalendarId = nextValue as ExternalProvider;
        }
        if (enabledCalendarAccount?.provider === "Google") {
          this.loadGoogleCalendars();
        }
      },
    },
  },
});
